// ─────────────────────────────────────────────────────────────────────────────
// Tour — interactive walkthrough engine. The first-run tutorial used to be a
// static slide deck; this version drops users into the actual app with a
// dimmed backdrop, a single highlight ring around the element being
// described, and a tooltip with field-guide copy.
//
// The driver is split into:
//   - tour.ts (this file) — script + state + simple pub/sub.
//   - TourOverlay.tsx     — the always-mounted renderer.
//   - Tutorial.tsx        — the welcome screen that boots the tour.
//
// Steps reference DOM elements by `data-tour="<id>"`. Adding a step means
// (a) pushing a TourStep here and (b) tagging the element on its page.
// ─────────────────────────────────────────────────────────────────────────────

export type TourStepKind = "spotlight" | "intro" | "finale";

export interface TourStep {
  id: string;
  kind: TourStepKind;
  // Where the user must be for this step to make sense. Driver navigates
  // here automatically before showing the coachmark.
  route: string;
  // The element to highlight. Omitted for `intro` and `finale` — those just
  // show a centered card. Use `data-tour="..."` on the target.
  target?: string;
  // Field-guide copy. Title is short; body is one sentence, plain English.
  kicker: string;
  title: string;
  body: string;
  // Optional: a "do this" hint shown under the body (e.g. "Tap the camera").
  hint?: string;
}

// Compact, friendly script. Each step is one idea — no walls of text.
export const TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    kind: "intro",
    route: "/home",
    kicker: "Field kit · 01 of 12",
    title: "Welcome to Fig.",
    body:
      "Fig is a pocket field guide for vintage. Point your camera at a garment and we read the labels, the seams, the era — then tell you what it's worth. Let's take a quick tour.",
  },
  {
    id: "dock-scan",
    kind: "spotlight",
    route: "/home",
    target: "[data-tour='dock-scan']",
    kicker: "Field kit · 02 of 12",
    title: "This is your scan button.",
    body:
      "It lives at the bottom of every screen. Tap it whenever you're standing in front of something you want Fig to read.",
    hint: "Tap Next when you've spotted it.",
  },
  {
    id: "dock-feed",
    kind: "spotlight",
    route: "/home",
    target: "[data-tour='dock-feed']",
    kicker: "Field kit · 03 of 12",
    title: "Feed shows what other collectors find.",
    body:
      "Real scans from real people. Follow a collector to see their pulls in your feed; tap a scan to read its full file.",
  },
  {
    id: "dock-grails",
    kind: "spotlight",
    route: "/home",
    target: "[data-tour='dock-grails']",
    kicker: "Field kit · 04 of 12",
    title: "Grails are pieces you're hunting.",
    body:
      "Add the thing you've wanted for years — a 70s Pendleton, a Big E 501, that scarf. When someone in the community posts a match, Fig pings you here.",
  },
  {
    id: "dock-you",
    kind: "spotlight",
    route: "/home",
    target: "[data-tour='dock-you']",
    kicker: "Field kit · 05 of 12",
    title: "You is your closet.",
    body:
      "Every scan you make gets archived here — kept, listed, or sold. It's also where your messages, settings, and field-kit references live.",
  },
  {
    id: "verdict",
    kind: "intro",
    route: "/home",
    kicker: "Field kit · 06 of 12",
    title: "Every scan gets a verdict.",
    body:
      "Seven possible reads — from FIG Verified down to Not Vintage — each one telling you what to do next. The full legend lives in Settings → Tips → Verdict legend.",
  },
  {
    id: "price",
    kind: "intro",
    route: "/home",
    kicker: "Field kit · 07 of 12",
    title: "Live market comps, every time.",
    body:
      "Each scan pulls sold listings from Depop, Grailed, eBay, and Etsy. You see a low / suggested / high range so you know what to pay — and what to ask.",
  },
  {
    id: "list",
    kind: "intro",
    route: "/home",
    kicker: "Field kit · 08 of 12",
    title: "Selling? One tap to list.",
    body:
      "Every closet tile has an edit pencil in the bottom corner. Tap it to add a marketplace, drop the listing URL, or mark the piece sold — the tile flips from KEPT to LISTED automatically.",
  },
  {
    id: "shops",
    kind: "intro",
    route: "/home",
    kicker: "Field kit · 09 of 12",
    title: "Save your shops once.",
    body:
      "Link your Depop, Grailed, eBay, etc. in Settings. Your shops show up under SHOP AT on your profile so visitors can tap straight through to your listings — and listing a new piece auto-fills the URL with your username.",
  },
  {
    id: "closet-tabs",
    kind: "intro",
    route: "/home",
    kicker: "Field kit · 10 of 12",
    title: "Kept · Listed · Sold.",
    body:
      "Your closet sorts itself based on what you do. Listed tiles show the actual marketplace name (EBAY, DEPOP, ETSY) and tap straight through to your listing. Mark a piece sold and it slides into the Sold tab — no bookkeeping.",
  },
  {
    id: "feedback",
    kind: "intro",
    route: "/home",
    kicker: "Field kit · 11 of 12",
    title: "Tips, brands, FAQ — all in one place.",
    body:
      "Settings → Tips has everything reference: the brands FIG verifies, the field guide, the verdict legend, what's new, and a direct line to support. Replay this Field Kit from there anytime.",
  },
  {
    id: "finale",
    kind: "finale",
    route: "/home",
    kicker: "Field kit · 12 of 12",
    title: "You're ready.",
    body: "Pick a place to start. You can always replay this from your profile's field kit.",
  },
];

// ── Persistence ────────────────────────────────────────────────────────────

const STORAGE_KEY = (uid: string) => `fig.tour.done.${uid}`;

export const tourStore = {
  isDone(uid: string): boolean {
    try { return localStorage.getItem(STORAGE_KEY(uid)) === "1"; }
    catch { return true; /* fail open so we don't trap users */ }
  },
  markDone(uid: string) {
    try { localStorage.setItem(STORAGE_KEY(uid), "1"); } catch { /* quota */ }
  },
  reset(uid: string) {
    try { localStorage.removeItem(STORAGE_KEY(uid)); } catch { /* ignore */ }
  },
};

// ── Active-tour pub/sub ────────────────────────────────────────────────────
// The overlay subscribes; Tutorial.tsx (and the field-kit Restart button)
// call `startTour()`. We keep the API tiny — no Redux, no context.

type Listener = (idx: number | null) => void;
const listeners = new Set<Listener>();
let activeIdx: number | null = null;

export const tourBus = {
  start() { activeIdx = 0; listeners.forEach((l) => l(activeIdx)); },
  next() {
    if (activeIdx == null) return;
    activeIdx = activeIdx + 1 < TOUR_STEPS.length ? activeIdx + 1 : null;
    listeners.forEach((l) => l(activeIdx));
  },
  prev() {
    if (activeIdx == null || activeIdx === 0) return;
    activeIdx -= 1;
    listeners.forEach((l) => l(activeIdx));
  },
  stop() { activeIdx = null; listeners.forEach((l) => l(activeIdx)); },
  current(): number | null { return activeIdx; },
  subscribe(l: Listener): () => void {
    listeners.add(l);
    l(activeIdx);
    return () => { listeners.delete(l); };
  },
};